/**
 * Comic & Manga world · "Night city": a starry night over little rooftops with warm windows.
 * Zones (level 1 → 4): Lantern Alley (paper lanterns + cherry blossoms) → Neon Rooftops (pink / mint
 * signs) → Star Tower (a slim tower in the skyline) → Moon Bridge (the big moon over an arched bridge).
 * Far skyline = no outline, close to the sky; near rooftops / trees / bridge = outlined. The middle band
 * (≈ h*0.35..0.7) only has low rooftops behind the trail. Ink stays warm (#2A2140 family).
 */
import { rolePalette } from '@sketchling/core';
import type { Layer } from '../../Figure';
import { ellipse, poly, scallop } from '../../geom';
import { rect, rng, roundTree, rrect, spread, twinkle } from '../kit';
import type { WorldArt } from '../types';

const palette = rolePalette({
  moon: '#FFF1C1',
  moonShade: '#F2DFA0',
  haze: '#5A4F96',
  far: '#3A3F78',
  farLight: '#444A88',
  near: '#555BA0',
  nearDark: '#4A4F92',
  roofTop: '#2E3263',
  roofRed: '#B5607A',
  window: '#FFD98A',
  neonPink: '#FF8FB8',
  neonMint: '#7FE3E0',
  star: '#FFF1C1',
  blossom: '#F4B6CC',
  blossomDark: '#E596B4',
  bark: '#6E4E6A',
  lantern: '#FF9E7A',
  lanternCap: '#7A4A5C',
  string: '#8E86C8',
  street: '#4B4D8E',
  bridge: '#C77F8E',
  bridgeDark: '#9C5E72',
  water: '#3E4486',
  waterLight: '#6B6FB5',
});

/** A little gable house (front), outlined, with warm windows. */
function house(x: number, base: number, w: number, hgt: number, s: number, fill: string, roof: string, R: () => number): Layer[] {
  const top = base - hgt;
  const L: Layer[] = [
    { k: 'region', d: rect(x, top, w, hgt + 40 * s), fill, shade: 6 * s },
    { k: 'region', d: poly([[x - 6 * s, top + 2 * s], [x + w / 2, top - w * 0.42], [x + w + 6 * s, top + 2 * s]]), fill: roof, shade: false },
  ];
  for (let wy = top + 12 * s; wy < base - 4 * s; wy += 22 * s)
    for (let wx = x + 9 * s; wx < x + w - 18 * s; wx += 20 * s) if (R() < 0.7) L.push({ k: 'spot', d: rrect(wx, wy, 10 * s, 12 * s, 2 * s), fill: 'window', stage: 2, opacity: 0.95 });
  return L;
}

/** A sagging string of paper lanterns from (x0, y) to (x1, y). */
function lanterns(x0: number, x1: number, y: number, s: number): Layer[] {
  const sag = 18 * s;
  const mx = (x0 + x1) / 2;
  const L: Layer[] = [{ k: 'ink', d: `M${x0} ${y}Q${mx} ${y + sag * 2} ${x1} ${y}`, w: 0.5, color: 'string', taper: 'none' }];
  const n = Math.max(2, Math.round((x1 - x0) / (34 * s)));
  for (let i = 1; i < n; i++) {
    const t = i / n;
    const lx = x0 + (x1 - x0) * t;
    const ly = (1 - t) * (1 - t) * y + 2 * (1 - t) * t * (y + sag * 2) + t * t * y;
    L.push({ k: 'spot', d: ellipse(lx, ly + 9 * s, 10 * s, 9 * s), fill: 'window', opacity: 0.25, stage: 2 });
    L.push({ k: 'region', d: ellipse(lx, ly + 9 * s, 6 * s, 7 * s), fill: i % 2 ? 'lantern' : 'neonPink', shade: 2 * s });
    L.push({ k: 'region', d: rect(lx - 3 * s, ly + 1 * s, 6 * s, 2.4 * s), fill: 'lanternCap', shade: false, line: false });
  }
  return L;
}

export const ANIME_ART: WorldArt = {
  id: 'anime',
  palette,
  theme: {
    sky: '#2E3263',
    skyLow: '#5B4F92',
    zoneSky: ['#3A3470', '#2E3263', '#29305E', '#2E3263'],
    ink: '#FFF1DC',
    label: '#3A3F78',
    accents: ['#FF8FB8', '#7FE3E0', '#FFF1C1'],
    trail: { kind: 'lantern', color: '#6B6FB5', edge: '#2A2140', deco: '#FFD98A' },
    card: { bg: '#3A3F78', border: '#555BA0', shadow: '#252957' },
    node: { ring: '#FF8FB8', done: '#7FE3E0', disc: '#FFF8EC', locked: '#555BA0' },
    raster: { mid: 0.66, amp: 0.06, scrim: 'rgba(40,32,84,0.16)', portrait: { mid: 0.8, amp: 0.05 } },
    dark: true,
  },
  scene: ({ w, h, zones }) => {
    const s = h / 300;
    const R = rng(41);
    const z = (i: number) => zones[Math.min(i, zones.length - 1)] ?? [0, w];
    const mid = (i: number) => (z(i)[0] + z(i)[1]) / 2;
    const L: Layer[] = [];
    // stars + the big moon over the Moon Bridge zone
    for (const x of spread(0, w, 30 * s, 7)) L.push(twinkle(x, h * (0.03 + R() * 0.3), (1.4 + R() * 2.4) * s, 'star'));
    L.push({ k: 'spot', d: ellipse(mid(3), h * 0.17, 52 * s, 52 * s), fill: 'moon', opacity: 0.12, stage: 2 });
    L.push({ k: 'region', d: ellipse(mid(3), h * 0.17, 34 * s, 34 * s), fill: 'moon', shade: 8 * s, line: false });
    L.push({ k: 'spot', d: ellipse(mid(3) - 10 * s, h * 0.14, 6 * s, 5 * s), fill: 'moonShade', stage: 2 });
    L.push({ k: 'spot', d: ellipse(mid(3) + 12 * s, h * 0.21, 4 * s, 3.5 * s), fill: 'moonShade', stage: 2 });
    // pink horizon haze
    L.push({ k: 'region', d: rect(-20, h * 0.38, w + 40, h), fill: 'haze', shade: false, line: false });
    // Star Tower (far, no outline)
    const tx = mid(2);
    L.push({ k: 'region', d: poly([[tx - 14 * s, h * 0.5], [tx - 7 * s, h * 0.12], [tx + 7 * s, h * 0.12], [tx + 14 * s, h * 0.5]]), fill: 'farLight', shade: false, line: false });
    L.push({ k: 'region', d: rect(tx - 1.2 * s, h * 0.04, 2.4 * s, h * 0.09), fill: 'farLight', shade: false, line: false });
    L.push(twinkle(tx, h * 0.04, 6 * s, 'neonPink'));
    for (let k = 0; k < 4; k++) L.push({ k: 'spot', d: rect(tx - 2.5 * s, h * (0.17 + k * 0.07), 5 * s, 6 * s), fill: 'window', stage: 2, opacity: 0.8 });
    // far skyline (no outline), neon signs in the Neon Rooftops zone
    let x = -20;
    while (x < w + 20) {
      const bw = (36 + R() * 46) * s;
      const top = h * (0.3 + R() * 0.12);
      L.push({ k: 'region', d: rect(x, top, bw + 1, h), fill: R() < 0.5 ? 'far' : 'farLight', shade: false, line: false });
      for (let wy = top + 10 * s; wy < h * 0.55; wy += 16 * s) if (R() < 0.35) L.push({ k: 'spot', d: rect(x + bw * (0.2 + R() * 0.5), wy, 4 * s, 5 * s), fill: 'window', stage: 2, opacity: 0.55 });
      if (x > z(1)[0] && x < z(1)[1] && R() < 0.6) L.push({ k: 'ink', d: rrect(x + bw * 0.15, top - 22 * s, bw * 0.6, 14 * s, 5 * s).replace(/Z$/, ''), w: 0.9, color: R() < 0.5 ? 'neonPink' : 'neonMint', taper: 'none' });
      x += bw;
    }
    // Lantern Alley: strings of paper lanterns across the sky
    for (let lx = z(0)[0] - 10; lx < z(0)[1]; lx += 150 * s) lanterns(lx, lx + 150 * s, h * (0.1 + R() * 0.06), s).forEach((l) => L.push(l));
    // low near rooftops behind the trail (outlined, warm windows)
    x = -10;
    while (x < w + 20) {
      const bw = (54 + R() * 40) * s;
      const base = h * (0.74 + R() * 0.04);
      const hgt = (20 + R() * 16) * s;
      const inL2 = x > z(1)[0] && x < z(1)[1];
      house(x, base, bw - 10 * s, hgt, s, R() < 0.5 ? 'near' : 'nearDark', inL2 || R() < 0.3 ? 'roofRed' : 'roofTop', R).forEach((l) => L.push(l));
      x += bw + R() * 30 * s;
    }
    // street
    L.push({ k: 'region', d: rect(-20, h * 0.86, w + 40, h), fill: 'street', shade: false });
    // Moon Bridge: water + arched bridge in the foreground
    const [b0, b1] = z(3);
    L.push({ k: 'region', d: rect(b0, h * 0.88, b1 - b0, h), fill: 'water', shade: false, line: false });
    for (const wx of spread(b0 + 10, b1 - 10, 60 * s, 5)) L.push({ k: 'ink', d: `M${wx} ${h * 0.95}Q${wx + 10 * s} ${h * 0.93} ${wx + 20 * s} ${h * 0.95}`, w: 0.6, color: 'waterLight', taper: 'both' });
    const bc = mid(3);
    const bw = Math.min(160 * s, (b1 - b0) * 0.45);
    L.push({ k: 'region', d: `M${bc - bw} ${h + 4}C${bc - bw * 0.6} ${h * 0.84} ${bc + bw * 0.6} ${h * 0.84} ${bc + bw} ${h + 4}Z`, fill: 'bridge', shade: 4 * s });
    L.push({ k: 'ink', d: `M${bc - bw * 0.9} ${h * 0.94}C${bc - bw * 0.5} ${h * 0.8} ${bc + bw * 0.5} ${h * 0.8} ${bc + bw * 0.9} ${h * 0.94}`, w: 0.8, color: 'bridgeDark', taper: 'none' });
    for (const k of [-0.6, -0.3, 0, 0.3, 0.6]) L.push({ k: 'region', d: rrect(bc + bw * k - 2 * s, h * 0.85 - Math.abs(k) * 10 * s, 4 * s, 12 * s, 1.5 * s), fill: 'bridgeDark', shade: false });
    // cherry trees (Lantern Alley, and one at each zone border)
    for (const tx2 of spread(z(0)[0] + 30 * s, z(0)[1], 120 * s, 3)) roundTree(tx2, h * 0.99, 1.5 * s, R() < 0.5 ? 'blossom' : 'blossomDark', 'bark').forEach((l) => L.push(l));
    for (const [i, zz] of zones.entries()) if (i > 1) roundTree(zz[0], h * 0.99, 1.3 * s, 'blossom', 'bark').forEach((l) => L.push(l));
    // falling petals
    for (const px of spread(0, w, 70 * s, 13)) if (R() < 0.7) L.push({ k: 'spot', d: scallop(px, h * (0.5 + R() * 0.45), 2.2 * s, 1.6 * s, 4, R()), fill: 'blossom', stage: 2, opacity: 0.85 });
    return { vb: [w, h], layers: L, guides: [] };
  },
};
